import type { GeoData } from '@/models/interfaces/geo/GeoData'
import { LngLatBounds, type Map as MaplibreMap } from 'maplibre-gl'

/**
 * Builds MapLibre bounds from platform GeoData (coords, lat/long, or bbox).
 * Returns null when no usable geometry is present.
 */
export function getLngLatBoundsFromGeoData(geo: GeoData | null | undefined): LngLatBounds | null {
  if (geo == null) return null

  const bbox = geo.bbox
  if (Array.isArray(bbox) && bbox.length >= 4) {
    const [w, s, e, n] = bbox
    if ([w, s, e, n].every((v) => typeof v === 'number' && Number.isFinite(v))) {
      return new LngLatBounds([w, s], [e, n])
    }
  }

  const lng = geo.coords?.lng ?? geo.longitude ?? undefined
  const lat = geo.coords?.lat ?? geo.latitude ?? undefined
  if (typeof lng === 'number' && typeof lat === 'number' && Number.isFinite(lng) && Number.isFinite(lat)) {
    return new LngLatBounds([lng, lat], [lng, lat])
  }

  return null
}

/**
 * Zooms the map to the given GeoData. Single points use flyTo; bbox uses fitBounds.
 */
export function fitMapToPlatformGeoData(
  map: MaplibreMap | null | undefined,
  geo: GeoData | null | undefined,
): boolean {
  const bounds = getLngLatBoundsFromGeoData(geo)
  if (!map || !bounds) return false

  try {
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const isPoint = sw.lng === ne.lng && sw.lat === ne.lat
    if (isPoint) {
      map.flyTo({ center: [sw.lng, sw.lat], zoom: 14, duration: 900, essential: true })
    } else {
      map.fitBounds(bounds, { padding: 56, maxZoom: 16, duration: 900 })
    }
    return true
  } catch {
    return false
  }
}
